/* eslint-disable max-len */
import { useCallback, useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import { Card, MonthsGrid } from './styles';

import NoData from '../../../../../components/NoData';

import download from '../../../../../assets/images/icons/download.svg';
import calendar from '../../../../../assets/images/icons/calendar.svg';
import FilterRadioButton from '../../../../../components/FilterRadioButtons';
import MyModal from '../../../../../components/Modal';
import { useAppContext } from '../../../../../contexts/auth';
import DashboardService from '../../../../../services/affreightment/DashboardService';
import formatDate from '../../../../../utils/formatDate';
import ExportXlsx from '../../../../../utils/ExportXlsx';
import useApiCall from '../../../../../hooks/useApiCall';
import { AffreightmentRelResponse, WarningsType } from '../../types';

interface WarningsCardInterface {
  dashboardWarningsData: WarningsType;
  selectedWorkplace: { value: number, label: string };
  onFilteringByMonth: ({ locTrab, month, year }: { locTrab: number, month: string, year: number }) => Promise<void>;
}

export default function WarningsCard({
  dashboardWarningsData, selectedWorkplace, onFilteringByMonth,
}: WarningsCardInterface) {
  const currentDate = new Date().toJSON().slice(0, 10);
  const currentYear = currentDate.split('-')[0];
  const currentMonth = currentDate.split('-')[1];
  const months = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];
  const monthsMap: { [key: number]: string } = {
    1: 'Janeiro',
    2: 'Fevereiro',
    3: 'Março',
    4: 'Abril',
    5: 'Maio',
    6: 'Junho',
    7: 'Julho',
    8: 'Agosto',
    9: 'Setembro',
    10: 'Outubro',
    11: 'Novembro',
    12: 'Dezembro',
  };
  const monthsAbvMap: { [key: number]: string } = useMemo(() => ({
    1: 'Jan',
    2: 'Fev',
    3: 'Mar',
    4: 'Abr',
    5: 'Mai',
    6: 'Jun',
    7: 'Jul',
    8: 'Ago',
    9: 'Set',
    10: 'Out',
    11: 'Nov',
    12: 'Dez',
  }), []);
  const [modalShow, setModalShow] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [selectedYear, setSelectedYear] = useState(Number(currentYear));
  const [currentMonthBeingShow, setCurrentMonthBeingShow] = useState(currentMonth);
  const [currentYearBeingShow, setCurrentYearBeingShow] = useState<number>(Number(currentYear));

  const { userData, selectedCompany } = useAppContext();
  const { userCode, userProfile } = userData;
  const companyCode = selectedCompany.value;

  const { apiCall } = useApiCall();

  const downloadRel = useCallback(async () => {
    await toast.promise(apiCall({
      apiToCall: DashboardService.getRelsData,
      queryParams: { codUsu: userCode, perfilUsu: userProfile, codEmpresa: companyCode },
      reqBody: JSON.stringify({
        codViagem: 0,
        codRelatorio: 5,
        dataViagens: currentDate,
        mes: currentMonthBeingShow,
        ano: currentYearBeingShow,
        dataPesquisa: currentDate,
      }),
      actionAfterResponse: (response: AffreightmentRelResponse) => {
        if (response.usuAdvertencia) {
          const usuAdvertenciaRelData = response.usuAdvertencia.map((data) => ({
            'Local de trabalho': data.locTrab,
            CPF: data.cpf,
            Nome: data.nome,
            'Linha ida titular': data.linhaIda || 'Não possui titularidade em nenhuma linha',

            'Data ultimo embarque (linha ida titular)': (data.ultimoAcessoIda == '0001-01-01T00:00:00'
              ? 'Não foi identificado nenhum embarque'
              : formatDate(data.ultimoAcessoIda)),

            'Linha volta titular': data.linhaVolta || 'Não possui titularidade em nenhuma linha',

            'Data ultimo embarque (linha volta titular)': (data.ultimoAcessoVolta == '0001-01-01T00:00:00'
              ? 'Não foi identificado nenhum embarque'
              : formatDate(data.ultimoAcessoVolta)),

            'Tipo advertencia': data.adv,
          }));
          ExportXlsx({
            data: usuAdvertenciaRelData,
            filename: `Relatório de advertencias ${monthsAbvMap[Number(currentMonthBeingShow)]} - ${currentYearBeingShow}`,
          });
        }

        if (!response.usuAdvertencia) {
          toast.error('Não há dados para serem baixados');
        }
      },
      catchMessage: 'Não foi possível baixar o relatório de advertências',
    }), {
      pending: 'Sua planilha está sendo baixada',
      success: 'Sua planilha foi baixada!',
      error: 'Não foi possível baixar sua planilha',
    });
  }, [apiCall, userCode, userProfile, companyCode, currentDate, currentMonthBeingShow, currentYearBeingShow, monthsAbvMap]);

  return (
    <>
      <Card>
        <div className="card-title">
          <div>
            Advertências-
            {' '}
            <div>
              {monthsAbvMap[Number(currentMonthBeingShow)]}
              /
              {String(currentYearBeingShow)?.replace('20', '')}
            </div>
          </div>
          <div>
            <img
              src={calendar}
              alt="filtrarData"
              title="Filtrar por mês"
              onClick={() => setModalShow(true)}
            />
            <img src={download} alt="download" title="Baixar relatório excel" onClick={downloadRel} />
          </div>
        </div>

        {dashboardWarningsData && (
          <>
            <div className="card-main">
              <div>
                {dashboardWarningsData.qtdAdv}
              </div>
              <small>
                {(dashboardWarningsData.qtdAdv > 1 ? 'Usuários' : 'Usuário')}
              </small>
            </div>

            <footer>
              {(!dashboardWarningsData.AdvBaixou) && (
                <>
                  <div className="porcentagem-negativa">
                +
                    {' '}
                    {dashboardWarningsData.percAdv}
                %
                  </div>
                  <div>
                Em relação ao início da operação
                  </div>
                </>
              )}
              {(dashboardWarningsData.AdvBaixou) && (
                <>
                  <div className="porcentagem-positiva">
                -
                    {' '}
                    {dashboardWarningsData.percAdv}
                %
                  </div>
                  <div>
                Em relação à média do mês anterior
                  </div>
                </>
              )}
            </footer>
          </>
        )}

        {(!dashboardWarningsData) && (
          <NoData
            icon="emptyBox"
            label={(
              <>
              O local de trabalho
                {' '}
                <strong>{selectedWorkplace.label}</strong>
                {' '}
              não possui dados sobre as advertências.
              </>
            )}
          />
        )}
      </Card>

      <MyModal
        show={modalShow}
        type="action"
        title="Filtrar advertências por mês"
        modalBody={(
          <div className="modal-body-centered">
            <div className="year-selection">
              <FilterRadioButton
                onClick={() => {
                  setSelectedYear((Number(currentYear) - 1));
                  setSelectedMonth('');
                }}
                selected={selectedYear == (Number(currentYear) - 1)}
              >
                {String(Number(currentYear) - 1)}
              </FilterRadioButton>
              <FilterRadioButton
                onClick={() => {
                  setSelectedYear(Number(currentYear));
                  setSelectedMonth('');
                }}
                selected={selectedYear == Number(currentYear)}
              >
                {currentYear}
              </FilterRadioButton>
            </div>
            <MonthsGrid>
              {months.map((month) => (
                <FilterRadioButton
                  key={month}
                  onClick={() => setSelectedMonth(month)}
                  selected={month == selectedMonth}
                  invisible={
                    Number(`${selectedYear}${month}`)
                      > Number(`${currentYear}${currentMonth}`)
                  }
                >
                  {monthsMap[Number(month)]}
                </FilterRadioButton>
              ))}
            </MonthsGrid>
          </div>
        )}
        size="lg"
        onAction={async () => {
          await onFilteringByMonth({
            locTrab: selectedWorkplace.value,
            month: selectedMonth,
            year: selectedYear,
          });
          setCurrentMonthBeingShow(selectedMonth);
          setCurrentYearBeingShow(selectedYear);
          setModalShow(false);
        }}
        onClose={() => setModalShow(false)}
        actionButtonLabel="Aplicar filtro"
        closeButtonLabel="Cancelar"
      />
    </>
  );
}
